import { getNotasLocal, getListasLocal } from '../db.js';
import { createBarChart, createDonutChart, createSparklineChart } from '../charts.js';
import { escapeHTML } from '../security.js';
import { authManager, getSavedScope, setSavedScope } from '../auth-manager.js';
import { getCategoryIcon, isDiscretionaryItem } from '../categorizer.js';
import { renderPeriodFilterHTML, initPeriodFilter, filterNotasByPeriod, getSavedPeriodFilter } from '../period-filter.js';
import { calculateBudgetReconciliationAnalytics } from '../utils/reconciliation.js';

// Helper function to clean product descriptions from raw SEFAZ texts
function normalizeProductDesc(desc) {
  if (!desc) return 'Produto';
  let cleaned = desc
    .replace(/\s*Vl\.?\s*Total.*$/i, '')
    .replace(/\s*Total\s*[\d,\.]+/i, '')
    .replace(/\s*Vl\.?\s*[\d,\.]+/i, '')
    .replace(/\s*-\s*R\$.*$/i, '')
    .replace(/\s*R\$\s*[\d,\.]+/i, '')
    .trim();
  return cleaned.toLowerCase().replace(/(?:^|\s)\S/g, a => a.toUpperCase());
}

// Full matching key
function getProductMatchKey(desc) {
  const clean = normalizeProductDesc(desc);
  return clean.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Extract base product family without size
function getProductFamilyKey(desc) {
  const clean = normalizeProductDesc(desc);
  let family = clean.toLowerCase()
    .replace(/\d+\s*(g|kg|gr|gramas|l|ml|litros|un|pct|cx|340g|500g|200g|137g|150g|350g|30m)\b/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
  return family.length > 5 ? family : clean.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Extract numerical weight/volume
function parseItemWeightOrVolume(desc, qty = 1) {
  if (!desc) return null;
  const match = desc.match(/(\d+(?:[\.,]\d+)?)\s*(g|kg|gr|l|ml)\b/i);
  if (!match) return null;
  
  let val = parseFloat(match[1].replace(',', '.'));
  let unit = match[2].toLowerCase();

  if (unit === 'kg') val = val * 1000;
  if (unit === 'l') val = val * 1000;

  return { val, unit: (unit === 'l' || unit === 'ml') ? 'ml' : 'g', raw: `${match[1]}${unit}` };
}

export async function renderAnaliseView(container, param, syncManager) {
  let currentScope = getSavedScope();
  const savedPeriod = getSavedPeriodFilter();
  let allNotas = [];
  let allListas = [];

  async function loadData() {
    try {
      if (authManager.isAuthenticated()) {
        const [notasRes, listasRes] = await Promise.all([
          authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`),
          authManager.fetchWithAuth(`/api/listas?scope=${currentScope}`)
        ]);
        if (notasRes.ok) allNotas = await notasRes.json();
        else allNotas = await getNotasLocal();
        if (listasRes.ok) allListas = await listasRes.json();
        else allListas = await getListasLocal(currentScope);
      } else {
        allNotas = await getNotasLocal();
        allListas = await getListasLocal(currentScope);
      }
    } catch (e) {
      allNotas = await getNotasLocal();
      allListas = await getListasLocal(currentScope);
    }
  }

  await loadData();

  const isPersonal = currentScope === 'personal';
  const isFamily = currentScope === 'family';
  let activePillar = 'all';
  let currentPeriod = savedPeriod.period;
  let customStart = savedPeriod.customStart;
  let customEnd = savedPeriod.customEnd;
  let sparkChart = null;
  let barChart = null;
  let donutChart = null;

  container.innerHTML = `
    <div class="section-header" style="display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 1.25rem;">
      <div>
        <h2 class="section-title" style="margin: 0; font-size: clamp(1.25rem, 4.5vw, 1.6rem); font-weight: 800; color: #111827; line-height: 1.2;">Análise Financeira</h2>
        <p class="section-subtitle" style="margin: 0.15rem 0 0 0; font-size: 0.82rem; color: #64748B;">Inteligência de gastos, cesta e pilares</p>
      </div>
      <div class="scope-selector" id="scope-selector">
        <button class="scope-pill ${isPersonal ? 'active' : ''}" data-scope="personal">Pessoal</button>
        <button class="scope-pill ${isFamily ? 'active' : ''}" data-scope="family">Família</button>
      </div>
    </div>

    <!-- Filtros de Período -->
    ${renderPeriodFilterHTML(savedPeriod.period, savedPeriod.customLabel, 'analise-custom-period-modal')}

    <!-- Pilares de Inteligência Financeira -->
    <div class="pillar-selector-wrapper">
      <button class="pillar-nav-btn prev" id="btn-pillar-prev" title="Rolar para a esquerda" aria-label="Rolar para a esquerda">
        <span class="material-symbols-outlined" style="font-size: 20px;">chevron_left</span>
      </button>
      <div class="pillar-selector" id="pillar-selector">
        <button class="pillar-pill ${activePillar === 'all' ? 'active' : ''}" data-pillar="all">Visão Geral</button>
        <button class="pillar-pill ${activePillar === 'reconciliation' ? 'active' : ''}" data-pillar="reconciliation">Planejado vs. Real (Listas)</button>
        <button class="pillar-pill ${activePillar === 'prices' ? 'active' : ''}" data-pillar="prices">Preços &amp; Inflação <span class="badge-pro" style="margin-left: 0.25rem;"><span class="material-symbols-outlined">diamond</span>PRO</span></button>
        <button class="pillar-pill ${activePillar === 'pilar1' ? 'active' : ''}" data-pillar="pilar1">Comparador de Cesta</button>
        <button class="pillar-pill ${activePillar === 'pilar2' ? 'active' : ''}" data-pillar="pilar2">Hábitos &amp; Recorrência</button>
        <button class="pillar-pill ${activePillar === 'pilar3' ? 'active' : ''}" data-pillar="pilar3">Essenciais vs. Supérfluos</button>
        <button class="pillar-pill ${activePillar === 'pilar4' ? 'active' : ''}" data-pillar="pilar4">Meios de Pagamento (VA/VR)</button>
        <button class="pillar-pill ${activePillar === 'pilar5' ? 'active' : ''}" data-pillar="pilar5">Métricas Per Capita</button>
      </div>
      <button class="pillar-nav-btn next" id="btn-pillar-next" title="Rolar para a direita" aria-label="Rolar para a direita">
        <span class="material-symbols-outlined" style="font-size: 20px;">chevron_right</span>
      </button>
    </div>

    <div id="analise-content"></div>
  `;

  const contentContainer = container.querySelector('#analise-content');
  const periodFilters = container.querySelector('#period-filters');
  const scopeSelector = container.querySelector('#scope-selector');
  const pillarSelector = container.querySelector('#pillar-selector');
  const btnPillarPrev = container.querySelector('#btn-pillar-prev');
  const btnPillarNext = container.querySelector('#btn-pillar-next');

  // Pillar horizontal nav buttons
  btnPillarPrev?.addEventListener('click', () => {
    pillarSelector.scrollBy({ left: -220, behavior: 'smooth' });
  });
  btnPillarNext?.addEventListener('click', () => {
    pillarSelector.scrollBy({ left: 220, behavior: 'smooth' });
  });

  // Enable drag-to-scroll on pillar selector
  let isDown = false;
  let startX = 0;
  let scrollLeft = 0;

  pillarSelector.addEventListener('mousedown', (e) => {
    isDown = true;
    pillarSelector.style.cursor = 'grabbing';
    startX = e.pageX - pillarSelector.offsetLeft;
    scrollLeft = pillarSelector.scrollLeft;
  });

  pillarSelector.addEventListener('mouseleave', () => {
    isDown = false;
    if (pillarSelector) pillarSelector.style.cursor = 'grab';
  });

  pillarSelector.addEventListener('mouseup', () => {
    isDown = false;
    if (pillarSelector) pillarSelector.style.cursor = 'grab';
  });

  window.addEventListener('mouseup', () => {
    if (isDown) {
      isDown = false;
      if (pillarSelector) pillarSelector.style.cursor = 'grab';
    }
  });

  pillarSelector.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - pillarSelector.offsetLeft;
    const walk = (x - startX) * 1.5;
    pillarSelector.scrollLeft = scrollLeft - walk;
  });

  // Enable mouse wheel horizontal scrolling
  pillarSelector.addEventListener('wheel', (e) => {
    if (e.deltaY !== 0) {
      e.preventDefault();
      pillarSelector.scrollLeft += e.deltaY;
    }
  }, { passive: false });

  // Scope toggle listener
  scopeSelector.querySelectorAll('.scope-pill').forEach(btn => {
    btn.addEventListener('click', async () => {
      scopeSelector.querySelectorAll('.scope-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      currentScope = btn.getAttribute('data-scope');
      setSavedScope(currentScope);
      await loadData();
      renderContent();
    });
  });

  // Global Scope synchronization listener
  window.addEventListener('nfce-scope-changed', async (e) => {
    const targetScope = e.detail?.scope;
    if (targetScope && targetScope !== currentScope) {
      currentScope = targetScope;
      if (scopeSelector) {
        scopeSelector.querySelectorAll('.scope-pill').forEach(b => {
          b.classList.toggle('active', b.getAttribute('data-scope') === currentScope);
        });
      }
      await loadData();
      renderContent();
    }
  });

  // Pillar toggle listener
  pillarSelector.querySelectorAll('.pillar-pill').forEach(btn => {
    btn.addEventListener('click', () => {
      pillarSelector.querySelectorAll('.pillar-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activePillar = btn.getAttribute('data-pillar');
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      renderContent();
    });
  });

  function renderContent() {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Filter notas (1 Ano, 3 Meses, Este Mês, Esta Semana, Hoje, Personalizado)
    let filteredNotas = filterNotasByPeriod(allNotas, currentPeriod, customStart, customEnd);

    // Base Calculations
    let totalGasto = 0;
    let totalTributos = 0;
    const storeSpendMap = {};
    const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const dayTotals = [0, 0, 0, 0, 0, 0, 0];
    const categoryTotals = {};
    const productSpendMap = {};
    const exactProductPricesMap = {};
    const familyProductPricesMap = {};
    const paymentMethods = {};

    let essentialSpend = 0;
    let discretionarySpend = 0;
    const discretionaryItemsList = [];
    let voucherSpend = 0; // VA / VR / Meal vouchers
    let personalSpend = 0; // Pix, Card, Cash

    let bulkTripsCount = 0;
    let bulkTripsTotal = 0;
    let quickTripsCount = 0;
    let quickTripsTotal = 0;

    filteredNotas.forEach(n => {
      const rawStore = n.emitente?.razaoSocial || n.razaoSocial || 'Estabelecimento';
      const storeName = rawStore.toLowerCase().replace(/(?:^|\s)\S/g, a => a.toUpperCase());
      const notaValor = n.totais?.valorTotal || n.valorTotal || 0;
      totalGasto += notaValor;
      totalTributos += (n.totais?.tributos || n.tributos || 0);

      storeSpendMap[storeName] = (storeSpendMap[storeName] || 0) + notaValor;

      // Bulk trips (>= R$ 150) vs Quick trips (< R$ 150)
      if (notaValor >= 150) {
        bulkTripsCount++;
        bulkTripsTotal += notaValor;
      } else {
        quickTripsCount++;
        quickTripsTotal += notaValor;
      }

      // Day of week
      if (n.dataEmissao) {
        const dayIdx = new Date(n.dataEmissao).getDay();
        dayTotals[dayIdx] += notaValor;
      }

      // Payments
      (n.pagamentos || []).forEach(p => {
        const rawForma = p.forma || 'Outros';
        const forma = rawForma.toLowerCase();
        if (forma.includes('pagar') || forma.includes('itens') || forma.includes('tributo') || forma.includes('desconto') || forma.includes('troco') || forma.includes('valor total') || forma.includes('lei federal')) {
          return;
        }
        const val = p.valor || 0;
        paymentMethods[rawForma] = (paymentMethods[rawForma] || 0) + val;

        if (forma.includes('alimentac') || forma.includes('refeic') || forma.includes('va') || forma.includes('vr') || forma.includes('sodexo') || forma.includes('alelo') || forma.includes('ticket')) {
          voucherSpend += val;
        } else {
          personalSpend += val;
        }
      });

      // Items processing
      (n.itens || []).forEach(i => {
        const cat = i.categoriaNome || 'Outros';
        const itemVal = i.valorTotal || 0;
        const itemQty = i.quantidade || 1;
        const cleanDesc = normalizeProductDesc(i.descricao);
        const matchKey = getProductMatchKey(i.descricao);
        const familyKey = getProductFamilyKey(i.descricao);
        const sizeInfo = parseItemWeightOrVolume(cleanDesc, itemQty);
        const itemUnitVal = itemQty > 0 ? (itemVal / itemQty) : itemVal;

        categoryTotals[cat] = (categoryTotals[cat] || 0) + itemVal;

        // Essential vs Discretionary
        if (isDiscretionaryItem(i.descricao, cat)) {
          discretionarySpend += itemVal;
          discretionaryItemsList.push({
            desc: cleanDesc,
            valor: itemVal,
            qty: itemQty,
            cat: cat,
            data: n.dataEmissao
          });
        } else {
          essentialSpend += itemVal;
        }

        // Product Total Spend
        if (!productSpendMap[cleanDesc]) {
          productSpendMap[cleanDesc] = { totalVal: 0, totalQty: 0, unit: i.unidade || 'UN' };
        }
        productSpendMap[cleanDesc].totalVal += itemVal;
        productSpendMap[cleanDesc].totalQty += itemQty;

        // Exact Product Price Map (enriched with temporal fields for price analysis)
        if (!exactProductPricesMap[matchKey]) {
          exactProductPricesMap[matchKey] = {
            displayName: cleanDesc,
            unit: i.unidade || 'UN',
            sizeInfo,
            purchases: []
          };
        }
        exactProductPricesMap[matchKey].purchases.push({
          store: storeName,
          unitPrice: itemUnitVal,
          totalPrice: itemVal,
          qty: itemQty,
          date: n.dataEmissao,
          rawDate: n.dataEmissao ? new Date(n.dataEmissao) : new Date(0),
          dateStr: n.dataEmissao ? new Date(n.dataEmissao).toLocaleDateString('pt-BR') : ''
        });

        // Family Product Map (for comparing package sizes like 200g vs 500g)
        if (!familyProductPricesMap[familyKey]) {
          familyProductPricesMap[familyKey] = {
            familyKey,
            variants: []
          };
        }
        familyProductPricesMap[familyKey].variants.push({
          displayName: cleanDesc,
          unitPrice: itemUnitVal,
          sizeInfo,
          store: storeName,
          dateStr: n.dataEmissao ? new Date(n.dataEmissao).toLocaleDateString('pt-BR') : ''
        });
      });
    });

    const avgTicket = filteredNotas.length > 0 ? (totalGasto / filteredNotas.length) : 0;
    const topStore = Object.entries(storeSpendMap).sort((a, b) => b[1] - a[1])[0] || ['N/A', 0];

    // Previous month comparison for KPI card
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthNotas = allNotas.filter(n => {
      if (!n.dataEmissao) return false;
      const d = new Date(n.dataEmissao);
      return d.getMonth() === prevMonthDate.getMonth() && d.getFullYear() === prevMonthDate.getFullYear();
    });
    const totalPrevMonth = prevMonthNotas.reduce((sum, n) => sum + (n.totais?.valorTotal || n.valorTotal || 0), 0);
    let pctChange = 0;
    if (totalPrevMonth > 0) {
      pctChange = Math.round(((totalGasto - totalPrevMonth) / totalPrevMonth) * 100);
    }

    // Clean active chart instances
    if (sparkChart) sparkChart.destroy();
    if (barChart) barChart.destroy();
    if (donutChart) donutChart.destroy();

    // RENDER BY PILLAR
    if (activePillar === 'prices') {
      // PREÇOS & INFLAÇÃO TAB
      const priceHikesSameStore = [];
      const crossStoreSavings = [];
      const packageSizeEfficiency = [];

      // Process exact match products for price hikes and cross-store savings
      Object.values(exactProductPricesMap).forEach(itemData => {
        const purchases = itemData.purchases.sort((a, b) => a.rawDate - b.rawDate);
        if (purchases.length >= 2) {
          const storeMap = {};
          purchases.forEach(p => {
            if (!storeMap[p.store]) storeMap[p.store] = [];
            storeMap[p.store].push(p);
          });

          const uniqueStores = Object.keys(storeMap);

          // 1. Same-Store Price Hikes
          uniqueStores.forEach(store => {
            const storePurchases = storeMap[store];
            if (storePurchases.length >= 2) {
              const earliest = storePurchases[0];
              const latest = storePurchases[storePurchases.length - 1];
              const diff = latest.unitPrice - earliest.unitPrice;

              if (diff > 0.05) {
                const pctIncrease = ((diff / earliest.unitPrice) * 100).toFixed(0);
                priceHikesSameStore.push({
                  prodName: itemData.displayName,
                  storeName: store,
                  oldPrice: earliest.unitPrice,
                  oldDate: earliest.dateStr,
                  newPrice: latest.unitPrice,
                  newDate: latest.dateStr,
                  priceDiff: diff,
                  pctIncrease
                });
              }
            }
          });

          // 2. Cross-Store Savings
          if (uniqueStores.length >= 2) {
            let minPurchase = purchases[0];
            let maxPurchase = purchases[0];

            purchases.forEach(p => {
              if (p.unitPrice < minPurchase.unitPrice) minPurchase = p;
              if (p.unitPrice > maxPurchase.unitPrice) maxPurchase = p;
            });

            if (minPurchase.store !== maxPurchase.store) {
              const diff = maxPurchase.unitPrice - minPurchase.unitPrice;
              if (diff > 0.05) {
                const totalQtyPurchased = purchases.reduce((s, p) => s + p.qty, 0);
                const potentialSavings = diff * totalQtyPurchased;
                const pctDiff = ((diff / minPurchase.unitPrice) * 100).toFixed(0);

                crossStoreSavings.push({
                  prodName: itemData.displayName,
                  potentialSavings,
                  minPrice: minPurchase.unitPrice,
                  maxPrice: maxPurchase.unitPrice,
                  bestStore: minPurchase.store,
                  worstStore: maxPurchase.store,
                  pctDiff
                });
              }
            }
          }
        }
      });

      // 3. Package Size Efficiency
      Object.values(familyProductPricesMap).forEach(familyData => {
        const variantsWithWeight = familyData.variants.filter(v => v.sizeInfo && v.sizeInfo.val > 0);
        if (variantsWithWeight.length >= 2) {
          const computed = variantsWithWeight.map(v => {
            const costPer100 = (v.unitPrice / v.sizeInfo.val) * 100;
            return { ...v, costPer100 };
          });

          computed.sort((a, b) => a.costPer100 - b.costPer100);
          const cheapestVariant = computed[0];
          const expensiveVariant = computed[computed.length - 1];

          if (cheapestVariant.displayName !== expensiveVariant.displayName && (expensiveVariant.costPer100 - cheapestVariant.costPer100) > 0.2) {
            const pctSaving = (((expensiveVariant.costPer100 - cheapestVariant.costPer100) / expensiveVariant.costPer100) * 100).toFixed(0);
            packageSizeEfficiency.push({
              bestProd: cheapestVariant.displayName,
              bestPrice: cheapestVariant.unitPrice,
              bestUnitCost: cheapestVariant.costPer100,
              worstProd: expensiveVariant.displayName,
              worstPrice: expensiveVariant.unitPrice,
              worstUnitCost: expensiveVariant.costPer100,
              unitType: cheapestVariant.sizeInfo.unit,
              pctSaving
            });
          }
        }
      });

      crossStoreSavings.sort((a, b) => b.potentialSavings - a.potentialSavings);
      priceHikesSameStore.sort((a, b) => b.priceDiff - a.priceDiff);
      packageSizeEfficiency.sort((a, b) => b.pctSaving - a.pctSaving);

      const topCrossSavings = crossStoreSavings.slice(0, 5);
      const topPriceHikes = priceHikesSameStore.slice(0, 5);
      const topSizeEfficiency = packageSizeEfficiency.slice(0, 5);

      const topHikePct = topPriceHikes.length > 0 ? topPriceHikes[0].pctIncrease : null;
      const topHikeProd = topPriceHikes.length > 0 ? topPriceHikes[0].prodName : null;
      const totalPotentialSavings = topCrossSavings.reduce((s, item) => s + item.potentialSavings, 0);

      contentContainer.innerHTML = `
        <!-- KPIs Resumidos -->
        <div class="kpi-grid mb-lg">
          <div class="kpi-card">
            <div class="kpi-title">Maior Aumento Detectado</div>
            <div class="kpi-value font-mono" style="color: #EF4444;">${topHikePct ? `▲ +${topHikePct}%` : '—'}</div>
            <div class="kpi-trend text-muted" style="font-size: 0.75rem; word-break: break-word;">${topHikeProd ? escapeHTML(topHikeProd) : 'Sem dados no período'}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Economia Potencial Total</div>
            <div class="kpi-value font-mono" style="color: #10B981;">${totalPotentialSavings > 0 ? `R$ ${totalPotentialSavings.toFixed(2).replace('.', ',')}` : '—'}</div>
            <div class="kpi-trend text-muted" style="font-size: 0.75rem;">${totalPotentialSavings > 0 ? 'Itens idênticos em lojas distintas' : 'Sem dados no período'}</div>
          </div>
        </div>

        <!-- 1. Alta de Preços no Mesmo Mercado -->
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="color: #EF4444; font-size: 20px;">trending_up</span>
            <h3 class="chart-card-title" style="margin: 0;">Alta de Preços no Mesmo Mercado</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1rem 0;">Variação de preço para o mesmo produto no mesmo estabelecimento.</p>
          ${topPriceHikes.length === 0 ? `
            <div class="text-muted text-center" style="font-size: 0.85rem; padding: 1.5rem 0;">
              Nenhum aumento de preço detectado para o mesmo item e tamanho na mesma loja.
            </div>
          ` : topPriceHikes.map(h => `
            <div class="analysis-item-card hike-card">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem; flex-wrap: wrap; gap: 0.3rem;">
                <span class="analysis-item-title">${escapeHTML(h.prodName)}</span>
                <span class="analysis-badge-danger">▲ +${h.pctIncrease}% de aumento</span>
              </div>
              <div class="analysis-item-desc">
                No <strong>${escapeHTML(h.storeName)}</strong>, o item subiu de <span class="text-muted font-mono">R$ ${h.oldPrice.toFixed(2).replace('.', ',')}</span> (${h.oldDate}) para <span class="font-mono font-bold" style="color: #EF4444;">R$ ${h.newPrice.toFixed(2).replace('.', ',')}</span> (${h.newDate}).
                <br/>
                <span style="color: #EF4444; font-weight: 700; font-size: 0.82rem;">Aumento real de R$ ${h.priceDiff.toFixed(2).replace('.', ',')} por unidade</span>
              </div>
            </div>
          `).join('')}
        </div>

        <!-- 2. Economia Entre Mercados -->
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="color: #10B981; font-size: 20px;">savings</span>
            <h3 class="chart-card-title" style="margin: 0;">Economia Entre Mercados</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1rem 0;">Compare onde os mesmos produtos saem mais baratos.</p>
          ${topCrossSavings.length === 0 ? `
            <div class="text-muted text-center" style="font-size: 0.85rem; padding: 1.5rem 0;">
              Nenhum produto idêntico comprado em estabelecimentos diferentes no período selecionado.
            </div>
          ` : topCrossSavings.map(s => `
            <div class="analysis-item-card saving-card">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem; flex-wrap: wrap; gap: 0.3rem;">
                <span class="analysis-item-title">${escapeHTML(s.prodName)}</span>
                <span class="analysis-badge-success">${s.pctDiff}% mais barato</span>
              </div>
              <div class="analysis-item-desc">
                Compre no <strong>${escapeHTML(s.bestStore)}</strong> por <span class="font-mono font-bold" style="color: #059669;">R$ ${s.minPrice.toFixed(2).replace('.', ',')}</span> em vez de <span class="text-muted font-mono">R$ ${s.maxPrice.toFixed(2).replace('.', ',')}</span> no ${escapeHTML(s.worstStore)}.
                <br/>
                <span style="color: #059669; font-weight: 700; font-size: 0.82rem;">Economia potencial: R$ ${s.potentialSavings.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>
          `).join('')}
        </div>

        <!-- 3. Custo-Benefício por Tamanho de Embalagem -->
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="color: #3B82F6; font-size: 20px;">aspect_ratio</span>
            <h3 class="chart-card-title" style="margin: 0;">Custo-Benefício por Tamanho de Embalagem</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1rem 0;">Economia proporcional comparando tamanhos diferentes de um mesmo produto.</p>
          ${topSizeEfficiency.length === 0 ? `
            <div class="text-muted text-center" style="font-size: 0.85rem; padding: 1.5rem 0;">
              Compre tamanhos diferentes do mesmo produto para comparar o custo proporcional por grama/ml!
            </div>
          ` : topSizeEfficiency.map(e => `
            <div class="analysis-item-card efficiency-card">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem; flex-wrap: wrap; gap: 0.3rem;">
                <span class="analysis-item-title">${escapeHTML(e.bestProd)}</span>
                <span class="analysis-badge-info">${e.pctSaving}% mais vantajoso por 100${e.unitType}</span>
              </div>
              <div class="analysis-item-desc">
                A embalagem <strong>${escapeHTML(e.bestProd)}</strong> sai por <span class="font-mono font-bold" style="color: #2563EB;">R$ ${e.bestUnitCost.toFixed(2).replace('.', ',')}/100${e.unitType}</span> (Preço: R$ ${e.bestPrice.toFixed(2).replace('.', ',')}).
                <br/>
                Comparado a <strong>${escapeHTML(e.worstProd)}</strong> por <span class="text-muted font-mono">R$ ${e.worstUnitCost.toFixed(2).replace('.', ',')}/100${e.unitType}</span> (Preço: R$ ${e.worstPrice.toFixed(2).replace('.', ',')}).
              </div>
            </div>
          `).join('')}
        </div>
      `;
      return;
    }

    if (activePillar === 'pilar1') {
      // PILAR 1: Comparador de Cesta & Preços por Loja
      const topStoreComparisons = [];
      Object.entries(exactProductPricesMap).forEach(([key, info]) => {
        if (info.purchases.length >= 2) {
          const storeMinMax = {};
          info.purchases.forEach(p => {
            if (!storeMinMax[p.store] || p.unitPrice < storeMinMax[p.store]) {
              storeMinMax[p.store] = p.unitPrice;
            }
          });

          const storeEntries = Object.entries(storeMinMax);
          if (storeEntries.length >= 2) {
            storeEntries.sort((a, b) => a[1] - b[1]);
            const bestStore = storeEntries[0][0];
            const minPrice = storeEntries[0][1];
            const worstStore = storeEntries[storeEntries.length - 1][0];
            const maxPrice = storeEntries[storeEntries.length - 1][1];
            const pctDiff = minPrice > 0 ? (((maxPrice - minPrice) / minPrice) * 100).toFixed(0) : 0;

            if (pctDiff > 5) {
              topStoreComparisons.push({ prodName: info.displayName, bestStore, minPrice, worstStore, maxPrice, pctDiff });
            }
          }
        }
      });

      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #D97706; font-size: 20px;">shopping_cart_checkout</span>
            <h3 class="chart-card-title" style="margin: 0;">Pilar 1: Comparador de Cesta &amp; Preços por Loja</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Compara os preços unitários praticados pelos supermercados para os mesmos produtos consumidos pela sua família.
          </p>

          ${topStoreComparisons.length === 0 ? `
            <div class="card-subtle text-center text-muted" style="padding: 2rem;">
              <span class="material-symbols-outlined" style="font-size: 36px; margin-bottom: 0.5rem; color: var(--text-muted);">search_off</span>
              <p style="margin: 0; font-size: 0.85rem;">Nenhum produto idêntico comprado em estabelecimentos diferentes no período selecionado.</p>
            </div>
          ` : topStoreComparisons.map(c => `
            <div class="analysis-item-card neutral-card">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.45rem;">
                <span class="analysis-item-title">${escapeHTML(c.prodName)}</span>
                <span class="analysis-badge-success font-mono">Economia de ${c.pctDiff}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; font-size: 0.82rem;">
                <span class="text-muted">${escapeHTML(c.bestStore)}</span>
                <span class="font-mono font-bold" style="color: #10B981;">R$ ${c.minPrice.toFixed(2).replace('.', ',')} (Mais Barato)</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.82rem;">
                <span class="text-muted">${escapeHTML(c.worstStore)}</span>
                <span class="font-mono font-bold" style="color: #EF4444;">R$ ${c.maxPrice.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      return;
    }

    if (activePillar === 'pilar2') {
      // PILAR 2: Hábitos de Compra & Recorrência
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #3B82F6; font-size: 20px;">calendar_month</span>
            <h3 class="chart-card-title" style="margin: 0;">Pilar 2: Hábitos de Compra &amp; Recorrência</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Diferença entre grandes compras planejadas de abastecimento e idas picadas de conveniência ao mercado.
          </p>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem;">
            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #3B82F6; margin-bottom: 0.25rem;">Grandes Compras (Abastecimento)</div>
              <div style="font-size: 1.4rem; font-weight: 800; color: var(--text-main);">${bulkTripsCount} compras</div>
              <div style="font-size: 0.82rem; font-weight: 700; color: #10B981; margin-top: 0.2rem;">Total: R$ ${bulkTripsTotal.toFixed(2).replace('.', ',')}</div>
            </div>

            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #D97706; margin-bottom: 0.25rem;">Compras Picadas (Conveniência)</div>
              <div style="font-size: 1.4rem; font-weight: 800; color: var(--text-main);">${quickTripsCount} idas</div>
              <div style="font-size: 0.82rem; font-weight: 700; color: #D97706; margin-top: 0.2rem;">Total: R$ ${quickTripsTotal.toFixed(2).replace('.', ',')}</div>
            </div>
          </div>

          <div class="tip-box-blue">
            💡 <strong>Dica de Economia:</strong> Compras picadas de conveniência costumam conter até 20% de itens comprados por impulso. Planejar 1 a 2 idas de feira/abastecimento por mês ajuda a reduzir idas frequentes.
          </div>
        </div>
      `;
      return;
    }

    if (activePillar === 'pilar3') {
      // PILAR 3: Essenciais vs Supérfluos
      const pctDiscretionary = totalGasto > 0 ? ((discretionarySpend / totalGasto) * 100).toFixed(1) : 0;
      const pctEssential = totalGasto > 0 ? (100 - parseFloat(pctDiscretionary)).toFixed(1) : 100;
      
      // Sort items by value
      discretionaryItemsList.sort((a, b) => b.valor - a.valor);

      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #8B5CF6; font-size: 20px;">pie_chart</span>
            <h3 class="chart-card-title" style="margin: 0;">Pilar 3: Essenciais vs. Supérfluos</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Separação inteligente dos gastos da família entre alimentação/higiene essencial e compras por impulso/guloseimas.
          </p>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem;">
            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #10B981; margin-bottom: 0.25rem;">Itens Essenciais (${pctEssential}%)</div>
              <div style="font-size: 1.35rem; font-weight: 800; color: var(--text-main);">R$ ${essentialSpend.toFixed(2).replace('.', ',')}</div>
            </div>

            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #D97706; margin-bottom: 0.25rem;">Supérfluos / Impulso (${pctDiscretionary}%)</div>
              <div style="font-size: 1.35rem; font-weight: 800; color: #D97706;">R$ ${discretionarySpend.toFixed(2).replace('.', ',')}</div>
            </div>
          </div>

          <!-- Explicação das Categorias e Regras Inclusas -->
          <div class="card-subtle" style="margin-bottom: 1rem;">
            <div style="font-size: 0.82rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.6rem; display: flex; align-items: center; gap: 0.4rem;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: #D97706;">category</span>
              Quais categorias e itens compõem este saldo?
            </div>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.55rem; font-size: 0.78rem; line-height: 1.45;">
              <div style="background: var(--bg-surface); padding: 0.6rem 0.75rem; border-radius: 0.6rem; border: 1px solid var(--border-color); border-left: 3px solid #F59E0B;">
                🍫 <strong style="color: var(--text-main);">Doces &amp; Chocolates:</strong>
                <div class="text-muted" style="font-size: 0.72rem; margin-top: 0.15rem;">Chocolates, bombons, wafers, balas, chicletes, sobremesas e sorvetes.</div>
              </div>
              <div style="background: var(--bg-surface); padding: 0.6rem 0.75rem; border-radius: 0.6rem; border: 1px solid var(--border-color); border-left: 3px solid #F59E0B;">
                🍿 <strong style="color: var(--text-main);">Snacks &amp; Salgadinhos:</strong>
                <div class="text-muted" style="font-size: 0.72rem; margin-top: 0.15rem;">Batatas chips, salgadinhos de pacote, pipocas e biscoitos recheados.</div>
              </div>
              <div style="background: var(--bg-surface); padding: 0.6rem 0.75rem; border-radius: 0.6rem; border: 1px solid var(--border-color); border-left: 3px solid #F59E0B;">
                🥤 <strong style="color: var(--text-main);">Bebidas &amp; Alcoólicos:</strong>
                <div class="text-muted" style="font-size: 0.72rem; margin-top: 0.15rem;">Refrigerantes, energéticos, cervejas, vinhos e destilados.</div>
              </div>
              <div style="background: var(--bg-surface); padding: 0.6rem 0.75rem; border-radius: 0.6rem; border: 1px solid var(--border-color); border-left: 3px solid #F59E0B;">
                🛍️ <strong style="color: var(--text-main);">Compras por Impulso:</strong>
                <div class="text-muted" style="font-size: 0.72rem; margin-top: 0.15rem;">Itens supérfluos identificados pelo classificador inteligente.</div>
              </div>
            </div>
          </div>

          <!-- Detalhamento dos Itens de Impulso Comprados no Período -->
          ${discretionaryItemsList.length > 0 ? `
            <div class="card-subtle" style="margin-bottom: 1rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.65rem;">
                <div style="font-size: 0.82rem; font-weight: 700; color: var(--text-main); display: flex; align-items: center; gap: 0.35rem;">
                  <span class="material-symbols-outlined" style="font-size: 18px; color: #F59E0B;">shopping_bag</span>
                  Itens de Impulso Comprados (${discretionaryItemsList.length})
                </div>
                <span class="text-muted font-mono" style="font-size: 0.75rem;">Total: R$ ${discretionarySpend.toFixed(2).replace('.', ',')}</span>
              </div>
              <div style="display: flex; flex-direction: column; gap: 0.4rem; max-height: 220px; overflow-y: auto;">
                ${discretionaryItemsList.map(item => `
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.45rem 0.7rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.5rem; font-size: 0.8rem;">
                    <div style="display: flex; flex-direction: column; min-width: 0;">
                      <span style="color: var(--text-main); font-weight: 600; word-break: break-word;">${escapeHTML(item.desc)}</span>
                      <span class="text-muted" style="font-size: 0.72rem;">Qtd: ${item.qty} &bull; ${escapeHTML(item.cat)}</span>
                    </div>
                    <span class="font-mono font-bold" style="color: #D97706; margin-left: 0.5rem; flex-shrink: 0;">R$ ${item.valor.toFixed(2).replace('.', ',')}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <div class="tip-box-amber">
            🍫 <strong>Inteligência de Consumo:</strong> <strong>${pctDiscretionary}%</strong> do seu orçamento familiar de compras foi destinado a doces, salgadinhos e bebidas supérfluas. Reduzir 30% desse grupo gera uma economia direta de <strong>R$ ${(discretionarySpend * 0.3).toFixed(2).replace('.', ',')}</strong> no mês.
          </div>
        </div>
      `;
      return;
    }

    if (activePillar === 'pilar4') {
      // PILAR 4: Meios de Pagamento (VA/VR vs Próprio)
      const pctVoucher = totalGasto > 0 ? ((voucherSpend / totalGasto) * 100).toFixed(1) : 0;
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #059669; font-size: 20px;">credit_card</span>
            <h3 class="chart-card-title" style="margin: 0;">Pilar 4: Eficiência de Meios de Pagamento</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Proporção das compras cobertas por Vales Alimentação/Refeição (VA/VR) versus recursos próprios (Pix, Dinheiro, Cartão de Crédito).
          </p>

          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem;">
            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #10B981; margin-bottom: 0.25rem;">Cobertura VA / VR (${pctVoucher}%)</div>
              <div style="font-size: 1.35rem; font-weight: 800; color: #10B981;">R$ ${voucherSpend.toFixed(2).replace('.', ',')}</div>
            </div>

            <div class="card-subtle text-center">
              <div style="font-size: 0.78rem; font-weight: 700; color: #3B82F6; margin-bottom: 0.25rem;">Recursos Próprios (Pix/Crédito)</div>
              <div style="font-size: 1.35rem; font-weight: 800; color: var(--text-main);">R$ ${personalSpend.toFixed(2).replace('.', ',')}</div>
            </div>
          </div>
        </div>
      `;
      return;
    }

    if (activePillar === 'pilar5') {
      // PILAR 5: Métricas Per Capita & Equidade Familiar
      const perCapitaCost = totalGasto > 0 ? (totalGasto / 2) : 0;
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; gap: 0.45rem; margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #EA580C; font-size: 20px;">groups</span>
            <h3 class="chart-card-title" style="margin: 0;">Pilar 5: Métricas Per Capita &amp; Equidade Familiar</h3>
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Custo médio de mercado/alimentação por pessoa no grupo familiar.
          </p>

          <div class="card-subtle text-center" style="padding: 1.25rem; margin-bottom: 1rem;">
            <div style="font-size: 0.8rem; font-weight: 600; color: var(--text-muted); margin-bottom: 0.25rem;">Gasto Médio Per Capita</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: var(--text-main);">R$ ${perCapitaCost.toFixed(2).replace('.', ',')} <span style="font-size: 0.85rem; font-weight: 600; color: var(--text-muted);">/ membro</span></div>
          </div>
        </div>
      `;
      return;
    }

    if (activePillar === 'reconciliation') {
      // PILAR DE CONCILIAÇÃO: Planejado vs. Realizado (Listas x NFC-e)
      const reconData = calculateBudgetReconciliationAnalytics(allListas);
      const isPositiveDiff = reconData.totalDiff > 0.01;
      const isNegativeDiff = reconData.totalDiff < -0.01;

      let accuracyBadgeColor = '#10B981';
      let accuracyBadgeBg = 'rgba(16, 185, 129, 0.12)';
      let accuracyLabel = 'Excelente';
      if (reconData.globalAccuracyPct < 80) {
        accuracyBadgeColor = '#EF4444';
        accuracyBadgeBg = 'rgba(239, 68, 68, 0.12)';
        accuracyLabel = 'Atenção';
      } else if (reconData.globalAccuracyPct < 90) {
        accuracyBadgeColor = '#F59E0B';
        accuracyBadgeBg = 'rgba(245, 158, 11, 0.12)';
        accuracyLabel = 'Boa';
      }

      contentContainer.innerHTML = `
        <div class="chart-card mb-lg">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.4rem; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="color: #2563EB; font-size: 22px;">checklist_rtl</span>
              <h3 class="chart-card-title" style="margin: 0;">Planejado vs. Realizado (Listas x NFC-e)</h3>
            </div>
            ${reconData.reconciledCount > 0 ? `
              <span style="font-size: 0.75rem; font-weight: 800; background: ${accuracyBadgeBg}; color: ${accuracyBadgeColor}; border: 1px solid ${accuracyBadgeColor}33; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill);">
                ${reconData.globalAccuracyPct}% Acurácia (${accuracyLabel})
              </span>
            ` : ''}
          </div>
          <p class="text-muted" style="font-size: 0.8rem; margin: 0 0 1.25rem 0;">
            Acurácia e desvio financeiro comparando os orçamentos planejados nas listas de compras com os valores efetivamente pagos nos cupons fiscais.
          </p>

          <!-- KPI Summary Grid -->
          <div class="kpi-grid mb-lg">
            <div class="kpi-card">
              <div class="kpi-title">Acurácia Orçamentária</div>
              <div class="kpi-value font-mono" style="color: ${accuracyBadgeColor};">${reconData.reconciledCount > 0 ? `${reconData.globalAccuracyPct}%` : '--'}</div>
              <div class="kpi-trend text-muted" style="font-size: 0.75rem;">
                ${reconData.reconciledCount > 0 ? `${reconData.withinBudgetCount} no teto / ${reconData.overBudgetCount} com estouro` : 'Sem listas vinculadas'}
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-title">Total Planejado</div>
              <div class="kpi-value font-mono">R$ ${reconData.totalEstimated.toFixed(2).replace('.', ',')}</div>
              <div class="kpi-trend text-muted" style="font-size: 0.75rem;">estimativa das listas</div>
            </div>

            <div class="kpi-card">
              <div class="kpi-title">Total Pago (NFC-e)</div>
              <div class="kpi-value font-mono" style="color: #2563EB;">R$ ${reconData.totalActual.toFixed(2).replace('.', ',')}</div>
              <div class="kpi-trend text-muted" style="font-size: 0.75rem;">cupons fiscais vinculados</div>
            </div>

            <div class="kpi-card">
              <div class="kpi-title">Balanço / Desvio</div>
              <div class="kpi-value font-mono" style="color: ${isPositiveDiff ? '#EF4444' : (isNegativeDiff ? '#10B981' : 'var(--text-main)')};">
                ${reconData.totalDiff > 0 ? '+' : ''}R$ ${reconData.totalDiff.toFixed(2).replace('.', ',')}
              </div>
              <div class="kpi-trend ${isPositiveDiff ? 'trend-down' : 'trend-up'}" style="font-size: 0.75rem;">
                <span class="material-symbols-outlined" style="font-size: 14px;">${isPositiveDiff ? 'trending_up' : (isNegativeDiff ? 'trending_down' : 'remove')}</span>
                <span>${reconData.totalDiffPct > 0 ? '+' : ''}${reconData.totalDiffPct.toFixed(1)}% ${isPositiveDiff ? 'estouro' : (isNegativeDiff ? 'economia' : 'exato')}</span>
              </div>
            </div>
          </div>

          <!-- Insight Inteligente -->
          ${reconData.reconciledCount > 0 ? `
            <div style="background: rgba(37, 99, 235, 0.06); border: 1px solid rgba(37, 99, 235, 0.18); border-radius: 0.75rem; padding: 0.85rem 1rem; margin-bottom: 1.5rem; display: flex; align-items: flex-start; gap: 0.65rem;">
              <span class="material-symbols-outlined" style="color: #2563EB; font-size: 20px; flex-shrink: 0; margin-top: 0.1rem;">insights</span>
              <div style="font-size: 0.82rem; color: var(--text-main); line-height: 1.4;">
                ${isPositiveDiff ? `
                  Suas compras de supermercado fecharam em média <strong>${reconData.totalDiffPct.toFixed(1)}% acima do planejado</strong> (+R$ ${reconData.totalDiff.toFixed(2).replace('.', ',')}). Revise itens adicionais no carrinho antes de passar no caixa para evitar compras por impulso.
                ` : (isNegativeDiff ? `
                  Parabéns! Suas compras fecharam <strong>${Math.abs(reconData.totalDiffPct).toFixed(1)}% abaixo do teto orçado</strong> (-R$ ${Math.abs(reconData.totalDiff).toFixed(2).replace('.', ',')} de economia acumulada).
                ` : `
                  Excelente disciplina orçamentária! O valor pago nos cupons fiscais coincidiu precisamente com a soma das listas planejadas.
                `)}
              </div>
            </div>
          ` : ''}

          <!-- Comparativos por Lista -->
          <div style="margin-top: 1rem;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem;">
              <h4 style="margin: 0; font-size: 0.95rem; font-weight: 700; color: var(--text-main);">Listas de Compras Conciliadas</h4>
              <span style="font-size: 0.78rem; color: var(--text-muted);">${reconData.reconciledCount} ${reconData.reconciledCount === 1 ? 'lista' : 'listas'}</span>
            </div>

            ${reconData.reconciledCount === 0 ? `
              <div class="empty-state" style="padding: 2.5rem 1rem; text-align: center; background: var(--bg-surface-alt); border-radius: 0.75rem; border: 1px dashed var(--border-color);">
                <span class="material-symbols-outlined empty-icon" style="font-size: 40px; color: var(--text-muted); opacity: 0.6;">checklist</span>
                <div class="empty-title" style="font-weight: 700; margin-top: 0.5rem; color: var(--text-main);">Nenhuma lista vinculada a notas fiscais</div>
                <div class="empty-desc" style="font-size: 0.82rem; color: var(--text-muted); max-width: 380px; margin: 0.25rem auto 1rem auto;">
                  Vincule seus cupons fiscais às listas de compras abertas ou finalizadas para acompanhar o comparativo orçamentário em tempo real.
                </div>
                <a href="#notas" class="btn btn-primary" style="display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.45rem 1rem; font-size: 0.82rem; border-radius: var(--radius-pill); text-decoration: none;">
                  <span class="material-symbols-outlined" style="font-size: 16px;">receipt_long</span> Ver Minhas Notas
                </a>
              </div>
            ` : `
              <div style="display: flex; flex-direction: column; gap: 0.85rem;">
                ${reconData.items.map(item => {
                  const itemIsOver = item.diff > 0.01;
                  const itemIsUnder = item.diff < -0.01;
                  const maxVal = Math.max(item.estimated, item.actual, 1);
                  const estPct = Math.min(100, (item.estimated / maxVal) * 100);
                  const actPct = Math.min(100, (item.actual / maxVal) * 100);

                  const statusBg = itemIsOver ? 'rgba(239, 68, 68, 0.1)' : (itemIsUnder ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)');
                  const statusColor = itemIsOver ? '#EF4444' : (itemIsUnder ? '#10B981' : 'var(--text-muted)');
                  const statusText = itemIsOver 
                    ? `+R$ ${item.diff.toFixed(2).replace('.', ',')} (+${item.diffPct.toFixed(1)}%)`
                    : (itemIsUnder ? `-R$ ${Math.abs(item.diff).toFixed(2).replace('.', ',')} (${item.diffPct.toFixed(1)}%)` : 'No Orçamento');

                  return `
                    <div class="card-subtle" style="padding: 1rem; border-radius: 0.75rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt);">
                      <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.75rem; flex-wrap: wrap;">
                        <div>
                          <div style="display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;">
                            <a href="#lista/${item.id}" style="text-decoration: none; color: var(--text-main); font-weight: 700; font-size: 0.92rem;">${escapeHTML(item.name)}</a>
                            ${item.scope === 'family' ? `<span style="font-size: 0.65rem; font-weight: 800; background: rgba(99, 102, 241, 0.15); color: #6366F1; padding: 0.05rem 0.35rem; border-radius: var(--radius-pill);">Família</span>` : ''}
                            <span style="font-size: 0.65rem; font-weight: 700; background: rgba(100, 116, 139, 0.15); color: var(--text-muted); padding: 0.05rem 0.35rem; border-radius: var(--radius-pill); text-transform: capitalize;">${escapeHTML(item.type)}</span>
                          </div>
                          <div style="font-size: 0.74rem; color: var(--text-muted); margin-top: 0.15rem;">
                            ${item.createdAt ? new Date(item.createdAt).toLocaleDateString('pt-BR') : 'Data N/A'} &bull; ${item.linkedNotas.length} ${item.linkedNotas.length === 1 ? 'cupom fiscal vinculado' : 'cupons fiscais vinculados'}
                          </div>
                        </div>

                        <span style="font-size: 0.75rem; font-weight: 800; background: ${statusBg}; color: ${statusColor}; border: 1px solid ${statusColor}33; padding: 0.18rem 0.55rem; border-radius: var(--radius-pill);">
                          ${statusText}
                        </span>
                      </div>

                      <!-- Comparison Bars -->
                      <div style="display: flex; flex-direction: column; gap: 0.45rem; margin-bottom: 0.85rem;">
                        <div>
                          <div style="display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 600; color: var(--text-muted); margin-bottom: 0.2rem;">
                            <span style="display: inline-flex; align-items: center; gap: 0.25rem;">
                              <span class="material-symbols-outlined" style="font-size: 14px; color: #3B82F6;">assignment</span> Planejado (Estimado)
                            </span>
                            <span class="font-mono" style="color: var(--text-main);">R$ ${item.estimated.toFixed(2).replace('.', ',')}</span>
                          </div>
                          <div style="height: 6px; background: rgba(0,0,0,0.06); border-radius: var(--radius-pill); overflow: hidden;">
                            <div style="height: 100%; width: ${estPct}%; background: #3B82F6; border-radius: var(--radius-pill);"></div>
                          </div>
                        </div>

                        <div>
                          <div style="display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 600; color: var(--text-muted); margin-bottom: 0.2rem;">
                            <span style="display: inline-flex; align-items: center; gap: 0.25rem;">
                              <span class="material-symbols-outlined" style="font-size: 14px; color: ${itemIsOver ? '#EF4444' : '#10B981'};">receipt_long</span> Real Pago (NFC-e)
                            </span>
                            <span class="font-mono font-bold" style="color: ${itemIsOver ? '#EF4444' : '#10B981'};">R$ ${item.actual.toFixed(2).replace('.', ',')}</span>
                          </div>
                          <div style="height: 6px; background: rgba(0,0,0,0.06); border-radius: var(--radius-pill); overflow: hidden;">
                            <div style="height: 100%; width: ${actPct}%; background: ${itemIsOver ? '#EF4444' : '#10B981'}; border-radius: var(--radius-pill);"></div>
                          </div>
                        </div>
                      </div>

                      <!-- Linked Notes List -->
                      ${item.linkedNotas && item.linkedNotas.length > 0 ? `
                        <div style="border-top: 1px solid var(--border-color); padding-top: 0.6rem; display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center;">
                          <span style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">Notas:</span>
                          ${item.linkedNotas.map(ln => {
                            const nStore = ln.nota?.emitente?.razaoSocial || ln.nota?.razaoSocial || 'Cupom Fiscal';
                            const nVal = (ln.allocatedAmount || ln.nota?.totais?.valorTotal || ln.nota?.valorTotal || 0).toFixed(2).replace('.', ',');
                            return `
                              <a href="#nota/${ln.notaId}" style="text-decoration: none; font-size: 0.72rem; font-weight: 600; background: var(--bg-surface); color: var(--text-main); border: 1px solid var(--border-color); padding: 0.15rem 0.5rem; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.25rem;">
                                <span class="material-symbols-outlined" style="font-size: 13px; color: #2563EB;">receipt</span>
                                <span>${escapeHTML(nStore)} (R$ ${nVal})</span>
                              </a>
                            `;
                          }).join('')}
                        </div>
                      ` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            `}
          </div>
        </div>
      `;
      return;
    }

    // VISÃO GERAL (`all` - Default containing all existing metrics & charts)
    const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);
    const sortedProductSpend = Object.entries(productSpendMap).sort((a, b) => b[1].totalVal - a[1].totalVal).slice(0, 10);
    const reconData = calculateBudgetReconciliationAnalytics(allListas);

    contentContainer.innerHTML = `
      <!-- Summary KPI Grid -->
      <div class="kpi-grid mb-lg">
        <div class="kpi-card">
          <div class="kpi-title">Total Gasto</div>
          <div class="kpi-value font-mono">R$ ${totalGasto.toFixed(2).replace('.', ',')}</div>
          ${totalPrevMonth > 0 ? `
            <div class="kpi-trend ${pctChange <= 0 ? 'trend-up' : 'trend-down'}">
              <span class="material-symbols-outlined">${pctChange <= 0 ? 'trending_down' : 'trending_up'}</span>
              <span>${pctChange > 0 ? '+' : ''}${pctChange}% vs. mês anterior</span>
            </div>
          ` : `<div class="kpi-trend text-muted" style="font-size: 0.75rem;">${filteredNotas.length} notas no período</div>`}
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Ticket Médio</div>
          <div class="kpi-value font-mono">R$ ${avgTicket.toFixed(2).replace('.', ',')}</div>
          <div class="kpi-trend text-muted" style="font-size: 0.75rem;">por cupom fiscal</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Maior Estabelecimento</div>
          <div class="kpi-value" style="font-size: 1.05rem; word-break: break-word; line-height: 1.2;">${escapeHTML(topStore[0])}</div>
          <div class="kpi-trend text-muted font-mono" style="font-size: 0.75rem; color: #10B981; font-weight: 700;">R$ ${topStore[1].toFixed(2).replace('.', ',')}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Impostos Estimados</div>
          <div class="kpi-value font-mono">R$ ${totalTributos.toFixed(2).replace('.', ',')}</div>
          <div class="kpi-trend text-muted" style="font-size: 0.75rem;">tributos incidentes</div>
        </div>
      </div>

      <!-- Acurácia do Orçamento de Compras (Highlight) -->
      ${reconData.reconciledCount > 0 ? `
        <div class="chart-card mb-lg" style="background: linear-gradient(135deg, var(--bg-surface), var(--bg-surface-alt)); border-left: 4px solid #10B981; padding: 1.15rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.5rem; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="color: #10B981; font-size: 22px;">checklist_rtl</span>
              <h3 class="chart-card-title" style="margin: 0; font-size: 1rem;">Planejado vs. Realizado (Listas x NFC-e)</h3>
            </div>
            <span style="font-size: 0.75rem; font-weight: 800; background: rgba(16, 185, 129, 0.15); color: #10B981; border: 1px solid rgba(16, 185, 129, 0.25); padding: 0.15rem 0.55rem; border-radius: var(--radius-pill);">
              ${reconData.globalAccuracyPct}% Acurácia
            </span>
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; margin-top: 0.65rem;">
            <div>
              <div style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">Total Planejado</div>
              <div class="font-mono" style="font-size: 1.05rem; font-weight: 700; color: var(--text-main);">R$ ${reconData.totalEstimated.toFixed(2).replace('.', ',')}</div>
            </div>
            <div>
              <div style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">Total Pago na NF</div>
              <div class="font-mono" style="font-size: 1.05rem; font-weight: 700; color: #2563EB;">R$ ${reconData.totalActual.toFixed(2).replace('.', ',')}</div>
            </div>
            <div>
              <div style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">Desvio Consolidado</div>
              <div class="font-mono font-bold" style="font-size: 1.05rem; color: ${reconData.totalDiff > 0.01 ? '#EF4444' : (reconData.totalDiff < -0.01 ? '#10B981' : 'var(--text-main)')};">
                ${reconData.totalDiff > 0 ? '+' : ''}R$ ${reconData.totalDiff.toFixed(2).replace('.', ',')} (${reconData.totalDiffPct > 0 ? '+' : ''}${reconData.totalDiffPct.toFixed(1)}%)
              </div>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Evolução dos Gastos (Directly below KPI cards) -->
      <div class="chart-card mb-lg">
        <h3 class="chart-card-title">Evolução dos Gastos</h3>
        <div class="chart-card-body" style="height: 200px; position: relative;">
          <canvas id="sparkline-canvas"></canvas>
        </div>
      </div>

      <!-- Gastos por Dia da Semana Bar Chart -->
      <div class="chart-card mb-lg">
        <h3 class="chart-card-title">Gastos por Dia da Semana</h3>
        <div style="height: 200px; position: relative;">
          <canvas id="day-bar-canvas"></canvas>
        </div>
      </div>

      <!-- Top 10 Products -->
      <div class="chart-card mb-lg">
        <h3 class="chart-card-title">Top 10 Produtos Mais Comprados</h3>
        <ul class="top-list">
          ${sortedProductSpend.map(([prod, data], idx) => {
            const avgPrice = data.totalQty > 0 ? (data.totalVal / data.totalQty) : data.totalVal;
            return `
              <li class="top-list-item">
                <span class="top-list-rank">${idx + 1}</span>
                <div style="flex: 1; min-width: 0;">
                  <div class="top-list-name" style="font-weight: 700; color: #111827;" title="${escapeHTML(prod)}">${escapeHTML(prod)}</div>
                  <div class="text-muted" style="font-size: 0.75rem;">${data.totalQty.toFixed(1)} ${data.unit} &bull; Média: R$ ${avgPrice.toFixed(2).replace('.', ',')}</div>
                </div>
                <span class="top-list-value font-mono font-bold" style="color: #111827;">R$ ${data.totalVal.toFixed(2).replace('.', ',')}</span>
              </li>
            `;
          }).join('')}
        </ul>
      </div>

      <!-- Categories breakdown (Last on this page) -->
      <div class="chart-card mb-lg">
        <h3 class="chart-card-title">Gastos por Categoria</h3>
        ${sortedCategories.length === 0 ? `
          <p class="text-muted text-center py-sm">Nenhum dado no período selecionado</p>
        ` : sortedCategories.map(([cat, val]) => {
          const pct = totalGasto > 0 ? ((val / totalGasto) * 100).toFixed(1) : 0;
          const icon = getCategoryIcon(cat);
          return `
            <div style="margin-bottom: 0.85rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; margin-bottom: 0.3rem;">
                <span class="font-medium" style="color: #111827;">${icon} ${escapeHTML(cat)}</span>
                <span class="font-mono text-muted">R$ ${val.toFixed(2).replace('.', ',')} (${pct}%)</span>
              </div>
              <div style="height: 6px; background: #ECEAE3; border-radius: var(--radius-pill); overflow: hidden;">
                <div style="height: 100%; width: ${pct}%; background: #D97706; border-radius: var(--radius-pill);"></div>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;

    // Render Sparkline Evolution Chart
    const sparkCtx = contentContainer.querySelector('#sparkline-canvas')?.getContext('2d');
    if (sparkCtx && filteredNotas.length > 0) {
      const dateMap = {};
      filteredNotas.forEach(nota => {
        if (!nota.dataEmissao) return;
        const dStr = nota.dataEmissao.split('T')[0];
        dateMap[dStr] = (dateMap[dStr] || 0) + (nota.totais?.valorTotal || nota.valorTotal || 0);
      });

      const sortedDates = Object.keys(dateMap).sort();
      const labels = sortedDates.map(d => {
        const parts = d.split('-');
        return `${parts[2]}/${parts[1]}`;
      });
      const data = sortedDates.map(d => dateMap[d]);

      sparkChart = createSparklineChart(sparkCtx, labels, data);
    }

    // Render Bar Chart
    const barCtx = contentContainer.querySelector('#day-bar-canvas')?.getContext('2d');
    if (barCtx) {
      barChart = createBarChart(barCtx, dayNames, dayTotals, 'Gastos por dia');
    }
  }

  // Initialize Period Filter Component
  initPeriodFilter(container, {
    initialPeriod: savedPeriod.period,
    initialCustomStart: savedPeriod.customStart,
    initialCustomEnd: savedPeriod.customEnd,
    initialCustomLabel: savedPeriod.customLabel,
    modalId: 'analise-custom-period-modal',
    onFilterChange: (period, start, end) => {
      currentPeriod = period;
      customStart = start;
      customEnd = end;
      renderContent();
    }
  });

  renderContent();

  // Listen for background tab resume sync update
  const handleSyncUpdate = async () => {
    const currentHash = window.location.hash || '#home';
    const route = currentHash.split('/')[0].replace('#', '');
    if (route !== 'analise') {
      window.removeEventListener('datasyncupdated', handleSyncUpdate);
      return;
    }
    try {
      await loadData();
      const checkHash = window.location.hash || '#home';
      const checkRoute = checkHash.split('/')[0].replace('#', '');
      if (checkRoute === 'analise') {
        renderContent();
      }
    } catch (e) {}
  };
  window.addEventListener('datasyncupdated', handleSyncUpdate);
}
